import { Button, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { PROFILE_LOCKED_BY_WARNING } from "../../consts/strings";
import {
  COLLECTION_BAR_DEFAULTS_CHECKBOX,
  COLLECTION_BAR_EDIT_TIMES,
} from "../../consts/sentryIds";
import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { COLLECTION_FIELDS } from "../../consts/collections";
import { CheckboxWithLabel } from "../CheckboxWithLabel";
import { View } from "../View";
import clsx from "clsx";
import { collectionTimeEditSelector } from "../../reducers/ownApp/selectors";
import { enableCollectionTimesEdit } from "../../reducers/ownApp/thunkActions";
import {
  getPlanCollectionDefaults,
  isEditModeSelector,
  matrixProfileLockSelector,
} from "../../reducers/matrixProfile/selectors";
import { palette } from "../../theme/palette";
import { setMarkerViewType, addNotification } from "../../reducers/ownApp";

const useStyles = makeStyles({
  container: {
    backgroundColor: palette.translucentTrueBlack,
    width: "100%",
    height: "48px",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0px 16px",
  },
  leftTextContainer: {
    display: "flex",
    flexDirection: "row",
  },
  barTextContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginRight: "11px",
  },
  barTextTitle: {
    fontWeight: "bold",
    fontSize: "12px",
    color: palette.typography.white.solid,
  },
  barText: {
    fontSize: "12px",
    marginLeft: "4px",
    color: palette.typography.white.high,
  },
  rightContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  editTimesButton: {
    background: palette.sunburst,
    border: "none",
    borderRadius: "4px",
    width: "65px",
    height: "17px",
    marginRight: "32px",
    marginLeft: "15px",
    padding: 0,
  },
  editTimesButtonText: {
    fontSize: "11px",
    lineHeight: "13px",
  },
  defaultsCheckbox: {
    margin: "auto",
  },
  disabled: {
    backgroundColor: `${palette.grey} !important`,
  },
});

export const CollectionDefaultBar = () => {
  const styles = useStyles();
  const collectionDefaults = useSelector(getPlanCollectionDefaults);
  const collectionTimeEdit = useSelector(collectionTimeEditSelector);
  const isRouteEditModeOn = useSelector(isEditModeSelector);
  const matrixProfileLock = useSelector(matrixProfileLockSelector);
  const dispatch = useDispatch();
  const [defaults, setDefaults] = useState(true);

  const renderBarText = useCallback(() => {
    const formatTimeLabel = (timeString) => {
      if (timeString) {
        return timeString.substring(0, 5);
      }
      return "--:--";
    };

    const formatBooleanLabel = (bool) => {
      return bool ? "Yes" : "No";
    };

    return COLLECTION_FIELDS.map((item) => {
      const { text, depotValueKey, type } = item;
      const value = collectionDefaults[depotValueKey];
      if (type === "text") {
        return (
          <View className={styles.barTextContainer} key={text}>
            <Typography className={styles.barTextTitle}>{text}</Typography>
            <Typography className={styles.barText}>
              {formatTimeLabel(value)}
            </Typography>
          </View>
        );
      } else if (type === "bool") {
        return (
          <View className={styles.barTextContainer} key={text}>
            <Typography className={styles.barTextTitle}>{text}</Typography>
            <Typography className={styles.barText}>
              {formatBooleanLabel(value)}
            </Typography>
          </View>
        );
      }
      return null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionDefaults]);

  const onEditTimesClick = () =>
    matrixProfileLock.lockedByUserCode && !matrixProfileLock.lockedByYou
      ? dispatch(
          addNotification({
            message: PROFILE_LOCKED_BY_WARNING(
              matrixProfileLock.lockedByUserName
            ),
            options: { variant: "info" },
          })
        )
      : dispatch(enableCollectionTimesEdit());

  return (
    <View className={styles.container}>
      <View className={styles.leftTextContainer}>{renderBarText()}</View>
      <View className={styles.rightContainer}>
        <View>
          <CheckboxWithLabel
            label="Defaults"
            checked={defaults}
            onChange={() => {
              if (!defaults) {
                dispatch(setMarkerViewType("D"));
              } else {
                dispatch(setMarkerViewType(false));
              }
              setDefaults(!defaults);
            }}
            disabled={false}
            sentryId={COLLECTION_BAR_DEFAULTS_CHECKBOX}
          />
        </View>
        <Button
          variant="contained"
          className={clsx(
            styles.editTimesButton,
            (collectionTimeEdit || isRouteEditModeOn) && styles.disabled
          )}
          onClick={onEditTimesClick}
          sentryId={COLLECTION_BAR_EDIT_TIMES}
          disabled={collectionTimeEdit || isRouteEditModeOn}
        >
          <Typography className={styles.editTimesButtonText}>
            Edit Times
          </Typography>
        </Button>
      </View>
    </View>
  );
};
